import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AnalyticsExportMenu } from "../shared/AnalyticsExportMenu";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";
import {
  REVENUE_CHART_CARD_PADDING,
  REVENUE_CHART_CARD_SHADOW,
} from "../revenue/constants";
import { SERVICES_CONTENT_GAP } from "../services/constants";
import { TeamStaffDetailKpiCards } from "./TeamStaffDetailKpiCards";
import { TeamStaffCancelledDelaysChart } from "./TeamStaffCancelledDelaysChart";
import { TeamStaffTasksTimeChart } from "./TeamStaffTasksTimeChart";
import type { TeamStaffDailyPoint } from "./staffDetailTypes";
import { TeamStaffProfileCard } from "./TeamStaffProfileCard";
import { getTeamStaffDetailAnalytics } from "./teamsDetailMappers";
import type { TeamSegment, TeamStaffRow } from "./types";

interface TeamStaffDetailViewProps {
  staff: TeamStaffRow;
  segment: TeamSegment;
  chartData: TeamStaffDailyPoint[];
  onBack: () => void;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const TeamStaffDetailView: React.FC<TeamStaffDetailViewProps> = ({
  staff,
  segment,
  chartData,
  onBack,
  onExport,
  exportDisabled = false,
}) => {
  const { t } = useTranslation();
  const analytics = useMemo(() => getTeamStaffDetailAnalytics(staff), [staff]);

  return (
    <div className="space-y-4 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="h-8 w-fit justify-start self-start rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("analytics.common.back", "Back")}
        </Button>
        {onExport ? (
          <AnalyticsExportMenu
            onExport={onExport}
            disabled={exportDisabled}
            className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
          />
        ) : null}
      </div>

      <div
        className={cn(
          "flex w-full flex-col rounded-2xl border border-[#E5E5E5] bg-white",
          REVENUE_CHART_CARD_SHADOW,
        )}
        style={{
          gap: SERVICES_CONTENT_GAP,
          padding: REVENUE_CHART_CARD_PADDING,
        }}
      >
        <TeamStaffProfileCard staff={staff} segment={segment} />

        <TeamStaffDetailKpiCards kpis={analytics.kpis} />

        <div className="grid w-full grid-cols-1 gap-4 xl:grid-cols-2">
          <TeamStaffTasksTimeChart data={chartData} />
          <TeamStaffCancelledDelaysChart data={chartData} />
        </div>
      </div>
    </div>
  );
};
